<template>
  <div>
    <div class="d-flex flex-wrap align-center mb-4" style="gap: 8px 12px">
      <h2 class="st-title">{{ $t("finance_tab_settlements") }}</h2>
      <span class="st-sub">{{ $t("finance_dynamics_label").toLowerCase() }}: {{ scheduleLabel }}</span>
      <v-spacer />
      <finance-export-menu :filename="exportFilename" :get-data="getExportData" :disabled="!rows.length" />
      <primary-button v-if="isAdmin && restaurantId" class="mt-6" @click="focusForm">
        {{ $t("finance_record_payment_generic") }}
      </primary-button>
    </div>

    <v-card outlined class="pa-5 mb-4">
      <div class="d-flex flex-wrap" style="gap: 12px 32px">
        <div>
          <div class="st-stat-label">{{ $t("finance_current_balance_label") }}</div>
          <div class="st-balance" :style="{ color: balanceColor }">
            <template v-if="balance !== null">
              {{ balance >= 0 ? $t("finance_hero_you_receive_short") : $t("finance_hero_you_owe") }}
              {{ money(Math.abs(balance), currency) }}
            </template>
            <template v-else>—</template>
          </div>
        </div>
        <div class="st-divider" />
        <div>
          <div class="st-stat-label">{{ $t("finance_next_settlement") }}</div>
          <div class="st-stat-value">{{ nextSettlementText }}</div>
        </div>
        <div class="st-divider" />
        <div>
          <div class="st-stat-label">{{ $t("finance_open_from_previous") }}</div>
          <div class="st-stat-value">{{ money(openFromPrevious, currency) }}</div>
        </div>
      </div>
    </v-card>

    <v-card outlined class="mb-4 st-table-card">
      <v-progress-linear v-if="loading" indeterminate color="primary" />
      <v-simple-table>
        <thead>
          <tr>
            <th>{{ $t("finance_period_label") }}</th>
            <th class="text-right">{{ $t("finance_turnover") }}</th>
            <th class="text-right">{{ $t("finance_deductions") }}</th>
            <th class="text-right">{{ $t("finance_net") }}</th>
            <th>{{ $t("finance_direction") }}</th>
            <th>{{ $t("finance_status_label") }}</th>
            <th>{{ $t("finance_document") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!rows.length">
            <td colspan="7" class="text-center grey--text py-6">
              {{ loading ? $t("finance_loading") : $t("finance_no_settlements") }}
            </td>
          </tr>
          <tr
            v-for="item in rows"
            :key="item.key"
            class="st-row"
            :class="{ 'st-row-open': item.status !== 'paid' && item.kind === 'payout', 'st-row-selected': selected && item.key === selected.key }"
            @click="selectedKey = item.key"
          >
            <td><a class="st-period">{{ item.periodLabel }}</a></td>
            <td class="text-right">{{ cell(item.turnover) }}</td>
            <td class="text-right">{{ cell(item.deductions) }}</td>
            <td class="text-right font-weight-bold">{{ money(item.net, currency) }}</td>
            <td>
              <span class="font-weight-bold" :style="{ color: dirColor(item) }">
                {{ item.direction === "to_restaurant" ? "→ " + $t("finance_dir_to_you") : "← " + $t("finance_dir_from_you") }}
              </span>
            </td>
            <td><span class="st-pill" :class="`st-pill-${statusTone(item)}`">{{ statusText(item) }}</span></td>
            <td><a class="st-pdf" @click.stop="printRow(item)">PDF</a></td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>

    <v-row v-if="selected || (isAdmin && restaurantId)">
      <v-col v-if="selected" cols="12" :md="isAdmin ? 7 : 12">
        <settlement-detail-panel :row="selected" @open-order="openOrder" @print="printRow(selected)" />
      </v-col>
      <v-col v-if="isAdmin && restaurantId" ref="formCol" cols="12" :md="selected ? 5 : 6">
        <record-payment-panel
          :direction.sync="recordDirection"
          :restaurant-id="restaurantId"
          :saving="saving"
          :reset-token="resetToken"
          @save="onRecord"
        />
      </v-col>
    </v-row>

    <finance-order-detail-dialog v-model="orderOpen" :order-id="orderId" />
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import restaurantFinanceService from "@/services/restaurantFinance";
import restaurantService from "@/services/restaurant";
import FinanceExportMenu from "./FinanceExportMenu.vue";
import FinanceOrderDetailDialog from "./FinanceOrderDetailDialog.vue";
import RecordPaymentPanel from "./dialogs/RecordPaymentPanel.vue";
import SettlementDetailPanel from "./dialogs/SettlementDetailPanel.vue";
import hasFinanceContext from "./hasFinanceContext";
import {
  FLOW_COLORS,
  PAYOUT_SCHEDULE_LABEL_KEYS,
  PAYOUT_STATUS_META,
  formatMoney,
  nextSettlementDate,
} from "./constants";

// "DD.MM.YYYY - DD.MM.YYYY" (tekst koji upisuje forma) -> {from, to} ISO.
// Stariji zapisi imaju slobodan tekst perioda - za njih razrada nije dostupna.
const PERIOD_RE = /(\d{1,2})\.(\d{1,2})\.(\d{4})\.?\s*[-–]\s*(\d{1,2})\.(\d{1,2})\.(\d{4})/;
const iso = (d, m, y) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

function parsePeriod(dto) {
  if (dto.period_from && dto.period_to) {
    return { from: String(dto.period_from).slice(0, 10), to: String(dto.period_to).slice(0, 10) };
  }
  const match = PERIOD_RE.exec(dto.period || "");
  return match ? { from: iso(match[1], match[2], match[3]), to: iso(match[4], match[5], match[6]) } : {};
}

function createdBy(dto) {
  const by = dto.created_by_name || dto.created_by || dto.recorded_by;
  return typeof by === "object" && by ? by.name : by || "";
}

// Isplate (Ordera -> restoran). GET /payouts vraća turnover/deductions/net u parama (1/100).
const mapPayout = (dto) => {
  const net = (Number(dto.net) || 0) / 100;
  return {
    key: `payout-${dto.id}`,
    kind: "payout",
    direction: "to_restaurant",
    date: dto.date,
    sortDate: dto.date || "",
    periodLabel: dto.period || "—",
    ...parsePeriod(dto),
    turnover: (Number(dto.turnover) || 0) / 100,
    deductions: (Number(dto.deductions) || 0) / 100,
    net,
    status: dto.status,
    method: dto.method || "",
    note: dto.note || dto.reference || "",
    createdBy: createdBy(dto),
    expected: net,
    paid: dto.status === "paid" ? net : 0,
  };
};

// Uplate restorana Orderi (restoran -> Ordera). GET /platform-payments -> payments[].
const mapPayment = (dto) => {
  const amount = Number(dto.amount) || 0;
  return {
    key: `payment-${dto.id}`,
    kind: "payment",
    direction: "from_restaurant",
    date: dto.date || dto.created_at,
    sortDate: dto.date || dto.created_at || "",
    periodLabel: dto.period || "—",
    ...parsePeriod(dto),
    turnover: null,
    deductions: null,
    net: -amount,
    status: "received",
    method: dto.method || "",
    note: dto.note || "",
    createdBy: createdBy(dto),
    expected: null,
    paid: amount,
  };
};

export default {
  name: "FinanceScreenSettlements",

  components: {
    FinanceExportMenu,
    FinanceOrderDetailDialog,
    RecordPaymentPanel,
    SettlementDetailPanel,
  },

  mixins: [hasSnackbar, hasFinanceContext],

  // Dijeli se sa ostalim ekranima kroz finance.vue; ovaj ekran period ne koristi.
  props: {
    period: { type: Object, default: null },
  },

  data: () => ({
    loading: false,
    saving: false,
    payouts: [],
    payments: [],
    balance: null,
    currency: "",
    contract: null,
    selectedKey: null,
    recordDirection: "payout",
    resetToken: 0,
    orderOpen: false,
    orderId: null,
  }),

  computed: {
    rows() {
      return [...this.payouts, ...this.payments].sort((a, b) =>
        String(b.sortDate).localeCompare(String(a.sortDate))
      );
    },
    // Izabran red; podrazumijevano najnoviji obračun.
    selected() {
      return this.rows.find((r) => r.key === this.selectedKey) || this.rows[0] || null;
    },
    // Prelazno: otvoreno = isplate koje nisu isplaćene, bez najnovijeg reda.
    openFromPrevious() {
      return this.rows
        .slice(1)
        .reduce((sum, r) => sum + (r.expected !== null ? Math.max(r.expected - r.paid, 0) : 0), 0);
    },
    balanceColor() {
      if (this.balance === null) return FLOW_COLORS.cash;
      return this.balance >= 0 ? FLOW_COLORS.positive : FLOW_COLORS.owing;
    },
    scheduleLabel() {
      const schedule = this.contract?.payout_schedule;
      if (!schedule) return this.$t("finance_schedule_not_agreed");
      if (schedule === "THRESHOLD" && this.contract.payout_threshold_amount != null) {
        return this.$t("finance_schedule_threshold_amount", {
          amount: this.money(this.contract.payout_threshold_amount, this.currency),
        });
      }
      return this.$t(PAYOUT_SCHEDULE_LABEL_KEYS[schedule] || "finance_schedule_not_agreed");
    },
    nextSettlementText() {
      const next = nextSettlementDate(this.contract?.payout_schedule);
      return next ? this.localeDate(next) : "—";
    },
    exportFilename() {
      return `obracuni_${this.restaurantId}`;
    },
  },

  watch: {
    restaurantId: "load",
  },

  created() {
    this.load();
  },

  methods: {
    cell(value) {
      return value === null || value === undefined ? "—" : formatMoney(value, this.currency);
    },
    dirColor(item) {
      return item.direction === "to_restaurant" ? FLOW_COLORS.positive : "#a65a00";
    },
    // Boja pilula: zelena = završeno, žuta = čeka
    statusTone(item) {
      if (item.kind === "payment") return "done";
      return item.status === "paid" ? "done" : "wait";
    },
    statusText(item) {
      if (item.kind === "payment") return `${this.$t("finance_status_payment_received")} ${this.shortDate(item.date)}`;
      if (item.status === "paid") return `${this.$t(PAYOUT_STATUS_META.paid.labelKey)} ${this.shortDate(item.date)}`;
      if (item.status === "pending") return this.$t("finance_status_confirmed_waiting");
      const meta = PAYOUT_STATUS_META[item.status];
      return meta ? this.$t(meta.labelKey) : item.status || "—";
    },
    shortDate(value) {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.`;
    },

    async load() {
      if (!this.restaurantId) return;
      this.loading = true;
      try {
        const [payoutsRes, paymentsRes, overviewRes, servicesRes] = await Promise.all([
          restaurantFinanceService.getPayouts(this.restaurantId),
          restaurantFinanceService.getPlatformPayments(this.restaurantId),
          // current_balance ne zavisi od perioda, pa se šalje bez ?from&to
          restaurantFinanceService.getFinancialOverview(this.restaurantId).catch(() => null),
          restaurantService.getRestaurantServices(this.restaurantId).catch(() => null),
        ]);
        this.payouts = (payoutsRes.data?.data || []).map(mapPayout);
        this.payments = (paymentsRes.data?.data?.payments || []).map(mapPayment);

        const overview = overviewRes?.data?.data || overviewRes?.data || {};
        this.balance = overview.current_balance != null ? Number(overview.current_balance) : null;
        this.currency = overview.currency || this.restaurantCurrency;

        const services = servicesRes?.data?.services || servicesRes?.data?.data?.services || [];
        const withContract = services.find((s) => s.is_active && s.contract?.payout_schedule);
        this.contract = withContract ? withContract.contract : null;
      } catch (error) {
        this.showErrorSnackbar(error?.response?.data?.message || this.$t("finance_settlements_load_error"));
      } finally {
        this.loading = false;
      }
    },

    openOrder(id) {
      this.orderId = id;
      this.orderOpen = true;
    },

    // "Evidentiraj plaćanje" u zaglavlju vodi na formu ispod
    focusForm() {
      const el = this.$refs.formCol && (this.$refs.formCol.$el || this.$refs.formCol);
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    // Prelazno nema generisanog PDF dokumenta - štampa stranice sa izabranim obračunom.
    printRow(item) {
      this.selectedKey = item.key;
      this.$nextTick(() => setTimeout(() => window.print(), 300));
    },

    async onRecord(payload) {
      this.saving = true;
      try {
        if (this.recordDirection === "payout") {
          await restaurantFinanceService.createPayout(this.restaurantId, payload);
          this.showSuccessSnackbar(this.$t("finance_payout_created"));
        } else {
          await restaurantFinanceService.createPlatformPayment(this.restaurantId, payload);
          this.showSuccessSnackbar(this.$t("finance_payment_saved"));
        }
        this.resetToken += 1;
        this.selectedKey = null;
        await this.load();
      } catch (error) {
        // 422: npr. iznos isplate veći od salda - backend poruka je najkorisnija.
        const response = error?.response?.data;
        const firstError = response?.errors ? Object.values(response.errors).flat()[0] : null;
        this.showErrorSnackbar(
          firstError ||
            response?.message ||
            this.$t(this.recordDirection === "payout" ? "finance_payout_create_error" : "finance_payment_save_error")
        );
      } finally {
        this.saving = false;
      }
    },

    getExportData() {
      return {
        headers: [
          this.$t("finance_period_label"),
          this.$t("finance_turnover"),
          this.$t("finance_deductions"),
          `${this.$t("finance_net")} (${this.currency})`,
          this.$t("finance_direction"),
          this.$t("finance_status_label"),
        ],
        rows: this.rows.map((r) => [
          r.periodLabel,
          r.turnover ?? "",
          r.deductions ?? "",
          r.net,
          r.direction === "to_restaurant" ? this.$t("finance_dir_to_you") : this.$t("finance_dir_from_you"),
          this.statusText(r),
        ]),
      };
    },
  },
};
</script>

<style scoped>
.st-title {
  font-size: 1.6rem;
  font-weight: 800;
  margin: 0;
}

.st-sub {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.6);
}

.st-stat-label {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.6);
}

.st-balance {
  font-size: 1.6rem;
  font-weight: 800;
}

.st-stat-value {
  font-size: 1.15rem;
  font-weight: 700;
  margin-top: 4px;
}

.st-divider {
  width: 1px;
  background: #e6e8ec;
}

.st-table-card ::v-deep th {
  font-size: 0.78rem !important;
  font-weight: 700 !important;
  color: rgba(0, 0, 0, 0.6) !important;
  height: 46px !important;
}

.st-row {
  cursor: pointer;
}

.st-row-open {
  background: #fff8ee;
}

.st-row-selected {
  box-shadow: inset 3px 0 0 #ff6e3a;
}

.st-period {
  font-weight: 700;
  text-decoration: underline;
}

.st-pdf {
  text-decoration: underline;
}

.st-pill {
  display: inline-block;
  border-radius: 999px;
  padding: 3px 12px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.st-pill-done {
  background: #e2f3e8;
  color: #1b6e3a;
}

.st-pill-wait {
  background: #fdf0d5;
  color: #8a5a00;
}
</style>
